import {
	isStreamInteractionConclusion,
	type StreamInteractionConclusion,
	type StreamMessage,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { isStoreAssistant } from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { getStoreMessageId } from './getStoreMessageId';
import type { ChatStoreContainerProps } from './types';
import { updateStoreAssistant } from './updateStoreAssistant';

type InteractionConclusionExtraction =
	| { kind: 'not-present' }
	| { kind: 'invalid' }
	| {
			kind: 'valid';
			transport: 'direct' | 'sse';
			value: StreamInteractionConclusion;
	  };

const extractInteractionConclusion = (
	streamObj: unknown,
	streamMessage: StreamMessage,
	defaultTransport: 'direct' | 'sse',
): InteractionConclusionExtraction => {
	if (
		typeof streamObj === 'object' &&
		streamObj !== null &&
		'eventType' in streamObj &&
		streamObj.eventType === 'INTERACTION_CONCLUSION'
	) {
		const data = 'data' in streamObj ? streamObj.data : undefined;
		const normalizedInteractionConclusion =
			typeof data === 'object' && data !== null
				? {
						...data,
						type: 'INTERACTION_CONCLUSION' as const,
					}
				: data;
		return isStreamInteractionConclusion(normalizedInteractionConclusion)
			? { kind: 'valid', transport: 'sse', value: normalizedInteractionConclusion }
			: { kind: 'invalid' };
	}

	if (
		typeof streamMessage === 'object' &&
		streamMessage !== null &&
		'type' in streamMessage &&
		streamMessage.type === 'INTERACTION_CONCLUSION'
	) {
		return isStreamInteractionConclusion(streamMessage)
			? { kind: 'valid', transport: defaultTransport, value: streamMessage }
			: { kind: 'invalid' };
	}

	return { kind: 'not-present' };
};

export const processInteractionConclusion = ({
	streamObj,
	streamMessage,
	conversationId,
	answerIndex,
	requestId,
	defaultTransport = 'direct',
	getMessages,
	setMessages,
	sendAnalyticsEvent,
}: {
	streamObj: unknown;
	streamMessage: StreamMessage;
	conversationId: string;
	answerIndex: number;
	requestId?: string;
	defaultTransport?: 'direct' | 'sse';
	getMessages: ChatStoreContainerProps['getMessages'];
	setMessages: ChatStoreContainerProps['setMessages'];
	sendAnalyticsEvent: ChatStoreContainerProps['sendAnalyticsEvent'];
}): void => {
	const extraction = extractInteractionConclusion(streamObj, streamMessage, defaultTransport);
	if (extraction.kind === 'not-present') {
		return;
	}

	if (extraction.kind !== 'valid') {
		return;
	}

	const interactionConclusion = extraction.value;
	const currentMessages = getMessages(conversationId);
	const targetMessage = currentMessages[answerIndex];

	if (
		!targetMessage ||
		!isStoreAssistant(targetMessage) ||
		targetMessage.interactionConclusionOutcome === interactionConclusion.message.outcome
	) {
		return;
	}

	const targetMessageId = getStoreMessageId(targetMessage);
	setMessages(
		conversationId,
		updateStoreAssistant({
			chat: currentMessages,
			promptId: targetMessageId,
			updateFunction: (storeMessage) => ({
				...storeMessage,
				interactionConclusionOutcome: interactionConclusion.message.outcome,
			}),
			updateType: 'Update Interaction Conclusion Outcome',
			useIndex: answerIndex,
		}),
	);

	sendAnalyticsEvent({
		eventType: 'track',
		action: 'received',
		actionSubject: 'interactionConclusionSignal',
		attributes: {
			conversationChannelId: conversationId,
			hasBackendMessageId: Boolean(interactionConclusion.metadata?.message_id),
			outcome: interactionConclusion.message.outcome,
			requestId: requestId ?? interactionConclusion.metadata?.request_id,
			transport: extraction.transport,
		},
	});
};
